import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { HeaderSection } from '@sorare/marketplace/src/components/market-home/HeaderSection';
import { SlideShow } from '@sorare/marketplace/src/components/market-home/SlideShow';

import { CollectionPreview } from '#src/components/collections/CollectionPreview/index.tsx';
import { useForCompetitionsCollectionFilter } from '#src/hooks/collections/useForCompetitionsCollectionFilter.ts';

import type {
  MyIncompleteCardCollectionsPaginatedQuery,
  MyIncompleteCardCollectionsPaginatedQueryVariables,
} from './__generated__/index.graphql.ts';

const MY_INCOMPLETE_CARD_COLLECTIONS_QUERY = gql`
  query MyIncompleteCardCollectionsPaginatedQuery(
    $user: String!
    $rarity: Rarity!
    $sport: Sport!
    $forCompetitions: [String!]
    $after: String
  ) {
    user(slug: $user) {
      slug
      cardCollections(
        sortType: LAST_CARD_ADDED
        startedOnly: true
        inSeasonOnly: true
        first: 20
        rarities: [$rarity]
        sport: $sport
        forCompetitions: $forCompetitions
        after: $after
      ) {
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
          ...CollectionPreview_cardCollection
        }
        seasonsCount {
          snapshotted
          season {
            startYear
            name
          }
        }
      }
    }
  }
  ${CollectionPreview.fragments.cardCollection}
` as TypedDocumentNode<
  MyIncompleteCardCollectionsPaginatedQuery,
  MyIncompleteCardCollectionsPaginatedQueryVariables
>;

const Row = styled(Horizontal)`
  gap: var(--double-unit);
  padding: var(--double-unit) 0;
  align-items: stretch;
`;

const Item = styled(Vertical).attrs({ center: true })`
  flex-shrink: 0;
  width: max(280px, calc(30 * var(--vw)));
  height: 100%;
  @media ${tabletAndAbove} {
    width: max(350px, calc(24 * var(--vw)));
  }
`;

const CollectionSkeleton = styled(Skeleton)`
  border-radius: var(--double-unit);
  width: max(280px, calc(30 * var(--vw)));
  aspect-ratio: 280/314;
  @media ${tabletAndAbove} {
    width: max(350px, calc(24 * var(--vw)));
    aspect-ratio: 350/332;
  }
`;

const SKELETONS = Array.from({ length: 12 }, (_, index) => ({ index }));

type Props = {
  so5CompetitionSlug: string;
  sport: Sport;
  rarity: Rarity;
  track?: ComponentProps<typeof CollectionPreview>['track'];
};

export const Collections = ({
  so5CompetitionSlug,
  sport,
  rarity,
  track,
}: Props) => {
  const { currentUser } = useCurrentUserContext();

  const { filterableSo5Competitions } = useSo5Competitions();
  const so5Competition = filterableSo5Competitions.find(
    c => c.slug === so5CompetitionSlug
  );
  const { forCompetitions } = useForCompetitionsCollectionFilter({
    competitionEligibility: so5CompetitionSlug,
    leagues: so5Competition?.competitions.map(c => c.slug),
  });

  const { data, loading, loadMore } = usePaginatedQuery(
    MY_INCOMPLETE_CARD_COLLECTIONS_QUERY,
    {
      variables: {
        rarity,
        user: currentUser?.slug || '',
        sport,
        forCompetitions,
      },
      skip: !currentUser?.slug,
      notifyOnNetworkStatusChange: true,
      connection: 'CardCollectionConnection',
    }
  );

  const pageInfo = data?.user?.cardCollections?.pageInfo;
  const hasNextPage = !!pageInfo?.hasNextPage;

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: pageInfo?.endCursor,
      });
    },
    hasNextPage,
    loading
  );
  const collections = data?.user?.cardCollections?.nodes || [];

  if (collections.length === 0 && !loading) {
    return null;
  }

  return (
    <Vertical gap={3}>
      <HeaderSection title={<FormattedMessage {...glossary.myCollections} />} />
      <SlideShow>
        <Row>
          {!!loading &&
            collections.length === 0 &&
            SKELETONS.map(s => <CollectionSkeleton key={s.index} />)}
          {!loading &&
            collections.map(cardCollection => (
              <Item key={cardCollection.slug}>
                <CollectionPreview
                  track={track}
                  collection={cardCollection}
                  to={generateSportPath(
                    ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
                    {
                      params: {
                        slug: currentUser?.slug || '',
                        collection: cardCollection.slug,
                      },
                      sport,
                    }
                  )}
                />
              </Item>
            ))}
          {!!hasNextPage && (
            <Item>
              <InfiniteScrollLoader />
            </Item>
          )}
        </Row>
      </SlideShow>
    </Vertical>
  );
};
